import React, {
  Dispatch,
  forwardRef,
  RefObject,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import classes from './TikTokSettings.module.scss';
import socialProfileClasses from '../SocialProfile/SocialProfile.module.scss';
import { TikTokProfileValues, TikTokProfileSettingsType } from '../SocialProfileListItem/SocialProfileListItem.view';
import { DropdownWrapper } from '../../containers/DropdownWrapper';
import { idGenerator } from '../../../helpers/idGenerator';
import { OutsideClickWrapper } from '../../actions/OutsideClickWrapper';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Select } from '../../forms/Select';
import { SelectItem } from '../../forms/Select/Select.view';
import { PostTikTokPrivacy, TikTokPrivacy } from '../../../models/graphql';
import { Checkbox } from '../../forms/Checkbox';
import classNames from 'classnames';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { Tooltip } from '../../actions/Tooltip';
import { BoxNotification } from '../../containers/BoxNotification';
import { Button } from '../../actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { useForwardedRefTransformer } from '../../../hooks/useForwardedRef';
import { SquareButton } from '../../actions/SquareButton';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { ErrorMessageSeverity } from '../../../constants/form';
import { Icon } from '../../images/Icon/Icon';
import { IconsEnum } from '../../../constants/icons';
import { hasSeverity } from '../../../modules/PostEditor/validators/helpers/severity-checker';
import { PostEditorContext } from '../../../modules/PostEditor/states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../../modules/PostEditor/states/PostEditorGroupDataProvider';
import { usePrevious } from 'primereact/hooks';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';

export type TikTokSettingsViewProps = {
  checked: boolean | undefined;
  disabled: boolean | undefined;
  expanded: boolean;
  loading: boolean;
  profileId: string;
  refreshTikTokProfileSettings: (id: string) => void;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  tikTokActionRules: RegisterOptions;
  tikTokActionValue?: TikTokProfileValues['allowedActions'];
  tikTokFormName: string;
  tikTokPromotionRules: RegisterOptions;
  tikTokPromotionValue?: TikTokProfileValues['promotion'];
  tikTokSettings: TikTokProfileSettingsType;
  tikTokTargetRules: RegisterOptions;
  tikTokTargetValue?: TikTokProfileValues['privacyTarget'];
  useFormInstance: UseFormReturn;
  parentDropdownRef?: RefObject<HTMLDivElement | null>;
  isParentDropdownExpanded: boolean;
};

const tikTokLink = (
  <a href={'https://www.tiktok.com/legal/page/global/music-usage-confirmation/en'} target={'_blank'} rel={'noreferrer'}>
    Music Usage Confirmation
  </a>
);

const brandedPolicy = (
  <a href={'https://www.tiktok.com/legal/page/global/bc-policy/en'} target={'_blank'} rel={'noreferrer'}>
    Branded Content Policy
  </a>
);

const isElementGuard = (node: Node): node is HTMLElement => {
  return node.nodeType === Node.ELEMENT_NODE;
};

const findParentWithId = (event: MouseEvent | TouchEvent | FocusEvent, idValue: string): HTMLElement | null => {
  let target = event.target as Node | null;
  while (target) {
    if (!isElementGuard(target)) return null;
    if (target.id && target.id === idValue) {
      return target;
    }
    target = target.parentElement; // Move to the parent element
  }
  return target;
};

export const TikTokSettingsView = forwardRef<HTMLDivElement | null, TikTokSettingsViewProps>(
  (props, ref): JSX.Element => {
    const {
      checked,
      disabled,
      expanded,
      loading,
      profileId,
      refreshTikTokProfileSettings,
      setExpanded,
      tikTokActionRules,
      tikTokActionValue,
      tikTokFormName,
      tikTokPromotionRules,
      tikTokPromotionValue,
      tikTokSettings,
      tikTokTargetRules,
      tikTokTargetValue,
      useFormInstance,
      parentDropdownRef,
      isParentDropdownExpanded
    } = props;
    const { privacy, commentDisabled, duetDisabled, stitchDisabled, maxVideoDuration: _ } = tikTokSettings;
    const inputID = useMemo(() => idGenerator(`tikTokProfile-${profileId}`), [profileId]);
    const dropDownRef = useForwardedRefTransformer(ref);

    const { state, profiles } = useContext(PostEditorContext);

    const [tabsState] = useContext(PostEditorGroupDataContext);
    const { postGroupIndex } = tabsState;
    const postGroup = state.postGroups[postGroupIndex];
    const { errors } = postGroup;
    const selectedProfiles = postGroup.profiles;
    const stringifiedSelectedProfilesIds = JSON.stringify(selectedProfiles.map(profile => profile.profileId) ?? []);
    const previousSelectedProfilesValue = usePrevious(stringifiedSelectedProfilesIds);
    const firstProfileInvalid = profilesHelpers.getFirstProfileInvalid(selectedProfiles);

    // TODO: Merge this logic into a single place when we move this to the cog icon
    const scrollToFirstInvalid = useCallback(() => {
      if (firstProfileInvalid && firstProfileInvalid?.profileId === profileId) {
        const profilesHTMLNodes =
          parentDropdownRef?.current?.firstElementChild?.lastElementChild?.lastElementChild?.children;
        const indexProfile = profiles.findIndex(profile => profile.id === firstProfileInvalid.profileId);
        const profileToScroll = profilesHTMLNodes?.item(indexProfile);
        const container = parentDropdownRef?.current;
        if (profileToScroll && container) {
          const elementRect = profileToScroll.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          const isElementInView = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

          if (!isElementInView) {
            profileToScroll.scrollIntoView({
              // type definition bugged. We need to upgrade typescript More info: https://github.com/microsoft/TypeScript/issues/46654
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              behavior: 'instant',
              block: 'nearest'
            });
          }
          setExpanded(true);
        }
      }
    }, [firstProfileInvalid, parentDropdownRef, profiles, setExpanded, profileId]);

    const shouldEnterScrollLogic =
      selectedProfiles.length === profiles.length &&
      previousSelectedProfilesValue &&
      previousSelectedProfilesValue !== stringifiedSelectedProfilesIds;
    useEffect(() => {
      if (isParentDropdownExpanded) {
        scrollToFirstInvalid();
      }
      // I dont want to put the scrollToFirstInvalid in the deps array
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isParentDropdownExpanded]);

    if (shouldEnterScrollLogic) {
      const parsedPrevious: string[] = JSON.parse(previousSelectedProfilesValue);

      if (parsedPrevious.length + 1 === selectedProfiles.length) {
        // This is an edge case that we ignore. Otherwise it will open the dropdown
      } else if (parsedPrevious.length !== profiles.length && parsedPrevious.length <= selectedProfiles.length) {
        scrollToFirstInvalid();
      }
    }

    const hasTikTokProfileWarning = hasSeverity(
      errors,
      ErrorMessageSeverity.warning,
      undefined,
      undefined,
      ['tikTokAudienceRequired'],
      profileId
    );

    const hasTikTokProfileError = hasSeverity(
      errors,
      ErrorMessageSeverity.error,
      undefined,
      undefined,
      ['tikTokAudienceRequired'],
      profileId
    );

    const profileError = hasTikTokProfileError ? 'Error' : '';
    const profileWarning = hasTikTokProfileWarning ? 'Warning' : '';

    const clickOnOutsideHandler = (event: MouseEvent | TouchEvent | FocusEvent) => {
      const parent = findParentWithId(event, `tikTokDropdownHandler_${profileId}`);
      if (parent) return;
      if (expanded) {
        setExpanded(false);
      }
    };

    const whoCanViewPostItems: SelectItem[] = useMemo(
      () => [
        privacy === TikTokPrivacy.PRIVATE
          ? { label: 'Followers', value: PostTikTokPrivacy.FOLLOWER_OF_CREATOR }
          : { label: 'Everyone', value: PostTikTokPrivacy.PUBLIC_TO_EVERYONE },
        { label: 'Friends', value: PostTikTokPrivacy.MUTUAL_FOLLOW_FRIENDS },
        {
          label: 'Only you',
          value: PostTikTokPrivacy.SELF_ONLY
        }
      ],
      [privacy]
    );

    let notificationText = <>By posting, you agree to TikTok&apos;s {tikTokLink}.</>;

    if (tikTokPromotionValue?.includes('other')) {
      notificationText = (
        <>
          Your post will be labeled as &apos;Paid partnership&apos;. By posting, you agree to TikTok&apos;s {tikTokLink}{' '}
          and {brandedPolicy}.
        </>
      );
    } else if (tikTokPromotionValue?.includes('self')) {
      notificationText = (
        <>
          Your post will be labeled as &apos;Promotional content&apos;. By posting, you agree to TikTok&apos;s{' '}
          {tikTokLink} and {brandedPolicy}.
        </>
      );
    }

    const allowUsersTooltip = (
      <div className={'cell shrink'}>
        <Tooltip
          disabled={loading}
          pointer
          target={`#tikTok_actions_${profileId}`}
          type={'inlined'}
          style={{ zIndex: scssVars['z-index-overmodal'] + 3 }}>
          {privacy === TikTokPrivacy.PUBLIC ? (
            <>
              {commentDisabled ? (
                <p className={'no-b-m'}>You have set the &apos;Comment&apos; interaction to &apos;No one&apos;.</p>
              ) : (
                <></>
              )}
              {duetDisabled ? (
                <p className={'no-b-m'}>You have set the &apos;Duet&apos; interaction to &apos;Only you&apos;.</p>
              ) : (
                <></>
              )}
              {stitchDisabled ? (
                <p className={'no-b-m'}>You have set the &apos;Stitch&apos; interaction to &apos;Only you&apos;.</p>
              ) : (
                <></>
              )}
              <p className={'no-b-m'}>
                You can edit your preferences in the TikTok mobile app, under Settings &rsaquo; Privacy &rsaquo;
                Interactions.
              </p>
            </>
          ) : (
            <>
              <p className={'no-b-m'}>
                Your profile is set to Private, so does not allow &apos;Duet&apos; or &apos;Stitch&apos; interaction.
                You can edit your preferences in the TikTok mobile app, under Settings &rsaquo; Privacy &rsaquo;
                Interactions.
              </p>
            </>
          )}
        </Tooltip>
      </div>
    );

    const disabledHandler = disabled || props.loading || !checked;

    return (
      <>
        <div className={classNames('cell', 'relative', socialProfileClasses.SquareButtonSizes)}>
          <div
            className={classNames(socialProfileClasses.SquareButtonWrapper)}
            id={`socialProfile_tikTok_${profileId}`}>
            <SquareButton
              id={`tikTokDropdownHandler_${profileId}`}
              // this id is used in TikTokProfileSettingsType.view to check if the handler has been clicked
              className={classNames(socialProfileClasses.IconStyles)}
              disabled={disabledHandler}
              action={e => {
                e.stopPropagation();
                // we want to stop propagation since outside click does not handle open / close
                setExpanded(!expanded);
              }}
              icon={SquareButtonIconsEnum.settings}
              type={'transparent'}
            />
            {!checked && (
              <Tooltip
                target={`#socialProfile_tikTok_${profileId}`}
                baseZIndex={+scssVars['z-index-overmodal'] + 1}
                pointer={true}
                type={'regular'}>
                Select the profile to access additional settings
              </Tooltip>
            )}
            {!expanded && (profileError || profileWarning) ? (
              <div
                className={classNames(
                  classes.ExclamationIconWrapper,
                  classes[`ExclamationIconWrapper${profileError || profileWarning}`]
                )}>
                <Icon name={IconsEnum.exclamation} size={'small'} />
              </div>
            ) : null}
          </div>
        </div>
        <OutsideClickWrapper
          onClickOutside={clickOnOutsideHandler}
          onFocusOutside={clickOnOutsideHandler}
          className={'grix-x cell small-12'}
          ref={dropDownRef}>
          <DropdownWrapper
            expanded={expanded}
            portalWrapperRef={dropDownRef}
            id={`portal-dropdown-${inputID}`}
            baseZIndex={`${+scssVars['z-index-overmodal'] + 1}`}>
            <div className={classes.TikTokSettings} onClick={e => e.stopPropagation()}>
              <Select
                dropDownZIndex={`${+scssVars['z-index-overmodal'] + 2}`}
                readonly={loading}
                hideDefaultSelectOption={true}
                chooseLabel={'Please select an audience'}
                items={whoCanViewPostItems}
                name={`${tikTokFormName}.privacyTarget`}
                rules={tikTokTargetRules}
                size={'large'}
                useFormInstance={useFormInstance}
                label={'Who can view this post'}
                {...(profileError || profileWarning
                  ? {
                      errorMessage: ' ',
                      errorSeverity: profileError ? ErrorMessageSeverity.error : ErrorMessageSeverity.warning
                    }
                  : {})}
                {...(tikTokTargetValue ? { selectedValue: tikTokTargetValue } : {})}
              />
              <div className={'grid-x align-middle margin-top-small-16'}>
                <div className={classNames('cell', 'shrink', classes.Label, { [classes.LabelDisabled]: loading })}>
                  Allow users to
                </div>
                {allowUsersTooltip}
              </div>
              {tikTokTargetValue === PostTikTokPrivacy.SELF_ONLY ? (
                <div className={'margin-top-small-20'}>
                  <BoxNotification type={'error'} disabled={loading}>
                    You can not select interactions if it&apos;s only visible for you.
                  </BoxNotification>
                </div>
              ) : (
                <div className={classNames('grid-x align-middle', classes.Checkboxes)}>
                  <div className={'cell shrink'}>
                    <Checkbox
                      formInstance={useFormInstance}
                      rules={tikTokActionRules}
                      readonly={loading || commentDisabled}
                      size={'large'}
                      label={'Comment'}
                      name={`${tikTokFormName}.comment`}
                      checked={tikTokActionValue?.includes('comment')}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <Checkbox
                      formInstance={useFormInstance}
                      rules={tikTokActionRules}
                      readonly={loading || duetDisabled}
                      size={'large'}
                      label={'Duet'}
                      name={`${tikTokFormName}.duet`}
                      checked={tikTokActionValue?.includes('duet')}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <Checkbox
                      formInstance={useFormInstance}
                      rules={tikTokActionRules}
                      readonly={loading || stitchDisabled}
                      size={'large'}
                      label={'Stitch'}
                      name={`${tikTokFormName}.stitch`}
                      checked={tikTokActionValue?.includes('stitch')}
                    />
                  </div>
                </div>
              )}
              <div className={'grid-x align-middle margin-top-small-16'}>
                <div className={classNames('cell', 'shrink', classes.Label, { [classes.LabelDisabled]: loading })}>
                  Contains promotion for
                </div>
                <div className={'cell shrink'}>
                  <Tooltip
                    disabled={loading}
                    pointer
                    target={`#tikTok_promotion${profileId}`}
                    type={'inlined'}
                    style={{ zIndex: scssVars['z-index-overmodal'] + 3 }}>
                    Does this post promote goods or services in exchange for something of value? It may promote
                    yourself, a third party, or both.
                  </Tooltip>
                </div>
              </div>
              {tikTokTargetValue !== PostTikTokPrivacy.SELF_ONLY ? (
                <div className={classNames('grid-x align-middle', classes.Checkboxes)}>
                  <div className={'cell shrink'}>
                    <Checkbox
                      formInstance={useFormInstance}
                      rules={tikTokPromotionRules}
                      readonly={loading}
                      size={'large'}
                      label={'Your brand'}
                      name={`${tikTokFormName}.self`}
                      checked={tikTokPromotionValue?.includes('self')}
                    />
                  </div>
                  <div className={'cell shrink'}>
                    <Checkbox
                      formInstance={useFormInstance}
                      rules={tikTokPromotionRules}
                      readonly={loading}
                      size={'large'}
                      label={'Another brand'}
                      name={`${tikTokFormName}.other`}
                      checked={tikTokPromotionValue?.includes('other')}
                    />
                  </div>
                </div>
              ) : (
                <div className={'margin-top-small-20'}>
                  <BoxNotification type={'error'} disabled={loading}>
                    You can not label this post as branded if it&apos;s only visible for you.
                  </BoxNotification>
                </div>
              )}
              <div className={'margin-top-small-20'}>
                <BoxNotification type={'info'} disabled={loading}>
                  {notificationText}
                </BoxNotification>
              </div>
              <div className={'grid-x align-middle margin-top-small-16'}>
                <div className={classNames('cell', 'shrink', classes.Label, { [classes.LabelDisabled]: loading })}>
                  Refresh from TikTok API
                </div>
                <div className={'cell shrink'}>
                  <Tooltip
                    disabled={loading}
                    pointer
                    target={`#tikTok_refresh${profileId}`}
                    type={'inlined'}
                    style={{ zIndex: scssVars['z-index-overmodal'] + 3 }}>
                    Get the latest privacy or interaction settings for your profile, in case you changed them in the
                    TikTok app.
                  </Tooltip>
                </div>
              </div>
              <div className={classes.TikTokButtonWrapper}>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  loading={loading}
                  color={ButtonColorsEnum.primary}
                  expanded={true}
                  action={e => {
                    e.stopPropagation();
                    refreshTikTokProfileSettings(profileId);
                  }}>
                  Refresh Profile Settings
                </Button>
              </div>
            </div>
          </DropdownWrapper>
        </OutsideClickWrapper>
      </>
    );
  }
);

TikTokSettingsView.displayName = 'TikTokSettingsView';
